import { Link } from 'react-router-dom';
import {
  FaBars,
  FaUser,
  FaBell,
  FaUsers,
  FaCreditCard,
  FaSignOutAlt,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import AiAssistantNavButton from './AiAssistantNavButton';

interface MobileTopNavProps {
  pageTitle: string;
  farmName?: string;
  userName?: string;
  userEmail?: string;
  userType?: string;
  userInitials: string;
  isFarmer: boolean;
  isRtl: boolean;
  aiChatOpen: boolean;
  onAiChatToggle: () => void;
  onOpenSidebar: () => void;
  showUserMenu: boolean;
  onToggleUserMenu: () => void;
  onCloseUserMenu: () => void;
  onCloseSidebar: () => void;
  onLogout: () => void;
  userMenuRef: React.RefObject<HTMLDivElement | null>;
}

export default function MobileTopNav({
  pageTitle,
  farmName,
  userName,
  userEmail,
  userType,
  userInitials,
  isFarmer,
  isRtl,
  aiChatOpen,
  onAiChatToggle,
  onOpenSidebar,
  showUserMenu,
  onToggleUserMenu,
  onCloseUserMenu,
  onCloseSidebar,
  onLogout,
  userMenuRef,
}: MobileTopNavProps) {
  const { t } = useTranslation('common');

  const closeMenus = () => {
    onCloseUserMenu();
    onCloseSidebar();
  };

  return (
    <div className="lg:hidden flex items-center gap-2 px-2.5 h-11 bg-white/85 backdrop-blur-xl border-b border-black/[0.06] supports-[backdrop-filter]:bg-white/75">
      {/* Menu */}
      <button
        type="button"
        onClick={onOpenSidebar}
        className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-[8px] text-[#3C3C43] active:bg-[#E5E5EA] touch-manipulation"
        aria-label="Open menu"
      >
        <FaBars className="h-4 w-4" />
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0 text-center px-1">
        <h1 className="text-[15px] font-semibold text-black leading-tight truncate tracking-[-0.01em]">
          {pageTitle}
        </h1>
        {farmName && (
          <p className="text-[10px] font-medium text-[#34C759] leading-tight truncate mt-px">
            {farmName}
          </p>
        )}
      </div>

      {/* Action cluster */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <div className="flex items-center h-8 px-0.5 rounded-[10px] bg-[#F2F2F7] border border-black/[0.04]">
          <LanguageSwitcher compact />
          <span className="w-px h-3.5 bg-black/10 flex-shrink-0" aria-hidden />
          <AiAssistantNavButton compact isOpen={aiChatOpen} onClick={onAiChatToggle} />
          <NotificationBell compact />
        </div>

        {/* Profile */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={onToggleUserMenu}
            className={`h-8 w-8 flex items-center justify-center rounded-full text-[11px] font-bold touch-manipulation transition-colors ${
              showUserMenu
                ? 'bg-[#007AFF] text-white ring-2 ring-[#007AFF]/30'
                : 'bg-gradient-to-br from-[#34C759] to-[#30B350] text-white'
            }`}
            aria-label={t('nav.myAccount')}
          >
            {userInitials}
          </button>

          {showUserMenu && (
            <div
              className={`absolute ${isRtl ? 'left-0' : 'right-0'} mt-2 w-52 max-w-[90vw] bg-white/95 backdrop-blur-xl border border-black/[0.08] rounded-[12px] shadow-xl z-[60] overflow-hidden`}
            >
              <div className="px-3.5 py-2.5 border-b border-[#F2F2F7] bg-[#FAFAFA]">
                <div className="font-semibold text-[14px] text-black truncate">{userName}</div>
                <div className="text-[11px] text-[#8E8E93] truncate">{userEmail}</div>
                {userType && (
                  <div className="text-[10px] text-[#34C759] capitalize mt-0.5">{userType}</div>
                )}
              </div>
              <div className="py-1">
                <Link
                  to="/account"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenus();
                  }}
                  className="flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-black active:bg-[#F2F2F7] touch-manipulation"
                >
                  <FaUser className="h-3.5 w-3.5 text-[#8E8E93]" />
                  {t('nav.myAccount')}
                </Link>
                <Link
                  to="/notification-settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenus();
                  }}
                  className="flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-black active:bg-[#F2F2F7] touch-manipulation"
                >
                  <FaBell className="h-3.5 w-3.5 text-[#8E8E93]" />
                  {t('nav.notificationSettings')}
                </Link>
                {isFarmer && (
                  <>
                    <Link
                      to="/team"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeMenus();
                      }}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-black active:bg-[#F2F2F7] touch-manipulation"
                    >
                      <FaUsers className="h-3.5 w-3.5 text-[#8E8E93]" />
                      {t('nav.teamMembers')}
                    </Link>
                    <Link
                      to="/account#billing"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeMenus();
                      }}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-black active:bg-[#F2F2F7] touch-manipulation"
                    >
                      <FaCreditCard className="h-3.5 w-3.5 text-[#8E8E93]" />
                      {t('nav.subscriptionAndBilling')}
                    </Link>
                  </>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenus();
                    onLogout();
                  }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-[#FF3B30] active:bg-[#FFF5F5] touch-manipulation border-t border-[#F2F2F7] mt-1"
                >
                  <FaSignOutAlt className="h-3.5 w-3.5" />
                  {t('nav.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
